import React, { useCallback, useMemo } from 'react';
import { Box, Field, FieldStateTypes } from '@common-fe/common-fe';
import _ from 'lodash';

import {
  COVERAGE_CODE_FIELD,
  CUSTOM_TRIGGER_AMOUNT_PREFIX,
  DEDUCTIBLE_FIELDS,
} from '../CoverageLevels/CoverageLevels.constants';
import { HEALTH_PLAN_SETUP_FIELD } from '../HealthPlanSetup.constants';
import { useIsEditableTemplateStore } from '../stores';
import HealthTemplateStateField from '../TemplateStateField';

import useHealthPlanSetupFieldsSetter from './useHealthPlanSetupFieldsSetter';
import useHealthPlanSetupFieldsSourceState from './useHealthPlanSetupFieldsSourceState';
import useHealthPlanSetupFieldsState from './useHealthPlanSetupFieldsState';
import useParentId from './useParentId';

const BLOCKED_FIELDS = ['parentTemplate', 'carrierId'];
const ALWAYS_DISABLED_STATE_FIELDS = [
  COVERAGE_CODE_FIELD,
  ...DEDUCTIBLE_FIELDS.map((f) => f.name),
] as string[];
const PRE_LABEL_NODE_DEFAULT_WIDTH = '56px';

const useTemplateFieldState = (
  fields: Field[],
  isTemplateMode?: boolean,
  isEditMode?: boolean,
  coverageId?: string,
  uniqStateNameField?: string
) => {
  const parentId = useParentId();
  const isParentIdExists = useMemo(() => !!parentId, [parentId]);
  const sourceState = useHealthPlanSetupFieldsSourceState();
  const state = useHealthPlanSetupFieldsState();
  const isEditableTemplate = useIsEditableTemplateStore((state) => state.isEditableTemplate);

  const { handleSetState } = useHealthPlanSetupFieldsSetter();

  const isMinimized = useMemo(() => !isTemplateMode || !isEditMode, [isEditMode, isTemplateMode]);

  const getModifiedFields = useCallback(
    (currentFields: Field[]): Field[] =>
      currentFields.map((field) => {
        const currentState = coverageId
          ? state.coverageLevels.find((item) => item.coverageId === coverageId) || state
          : state;

        const currentSourceState = coverageId
          ? sourceState.coverageLevels.find((item) => item.coverageId === coverageId) || sourceState
          : sourceState;

        const currentValue = _.get(currentState, `${field.name}`) as FieldStateTypes;
        const sourceValue = _.get(currentSourceState, `${field.name}`) as FieldStateTypes;
        const sourceDisabled =
          sourceValue === FieldStateTypes.Locked ||
          sourceValue === FieldStateTypes.Hidden ||
          isEditableTemplate;
        const appliedDisabling =
          ALWAYS_DISABLED_STATE_FIELDS.includes(field.name) ||
          field.name.startsWith(CUSTOM_TRIGGER_AMOUNT_PREFIX) ||
          field.name === HEALTH_PLAN_SETUP_FIELD.PLAN_SUMMARY ||
          (isParentIdExists || isEditableTemplate
            ? sourceDisabled
            : BLOCKED_FIELDS.includes(field.name) && sourceDisabled);

        if (uniqStateNameField && field.name === uniqStateNameField) {
          return {
            ...field,

            preLabelNode: (
              <Box width={{ min: PRE_LABEL_NODE_DEFAULT_WIDTH }}>
                <HealthTemplateStateField
                  testId={`${field.name}_state`}
                  minimized={isMinimized}
                  value={currentValue}
                  disabled={appliedDisabling}
                  onChangeValue={(value) =>
                    handleSetState(
                      {
                        [field.name]: value,
                      },
                      coverageId
                    )}
                />
              </Box>
            ),
          };
        }
        if (field.subFields && field.label && !uniqStateNameField) {
          return {
            ...field,
            subFields: getModifiedFields(field.subFields),
            preLabelNode: (
              <Box width={{ min: PRE_LABEL_NODE_DEFAULT_WIDTH }}>
                <HealthTemplateStateField
                  testId={`${field.name}_state`}
                  minimized={isMinimized}
                  disabled={appliedDisabling}
                  value={currentValue}
                  onChangeValue={(value) =>
                    handleSetState(
                      {
                        [field.name]: value,
                      },
                      coverageId
                    )}
                />
              </Box>
            ),
          };
        }
        if (field.label && !uniqStateNameField) {
          return {
            ...field,

            preLabelNode: (
              <Box width={{ min: PRE_LABEL_NODE_DEFAULT_WIDTH }}>
                <HealthTemplateStateField
                  testId={`${field.name}_state`}
                  minimized={isMinimized}
                  value={currentValue}
                  disabled={appliedDisabling}
                  onChangeValue={(value) =>
                    handleSetState(
                      {
                        [field.name]: value,
                      },
                      coverageId
                    )}
                />
              </Box>
            ),
          };
        }
        return {
          ...field,
          preLabelNode: <Box width={PRE_LABEL_NODE_DEFAULT_WIDTH} />,
        };
      }),
    [
      coverageId,
      handleSetState,
      isEditableTemplate,
      isMinimized,
      isParentIdExists,
      sourceState,
      state,
      uniqStateNameField,
    ]
  );
  return getModifiedFields(fields);
};

export default useTemplateFieldState;
